import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import styled from "styled-components";
import Loader from "react-loader-spinner";
import { Button, Snackbar } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import Select from "react-select";
import DbTableData from "./dbTableData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt, faFilter } from "@fortawesome/free-solid-svg-icons";
import FilterInterface from "./filterInterface";
import AddItemPage from "./addItemPage";
import { TablesWrapView } from "./tabalesWrapView";
import {
  getPost,
  getRequest,
  getDevToken,
  createApiTarget,
  classifyApiFailure,
  getUserFacingApiErrorMessage,
  ACCESS_MODES,
  API_FAILURE_KINDS,
  AUTHENTICATION_SCOPES,
} from "../utils/commHelper";
import { DbInterfaceSessionContext } from "./session/dbInterfaceSessionGate";
import DbInterfaceSessionBar from "./session/dbInterfaceSessionBar";
import DbInterfaceVpnBanner, { VPN_BANNER_VARIANTS } from "./dbInterfaceVpnBanner";
import {
  parseFiltersFromUrl,
  updateUrlWithFilters,
  migrateFromLegacyFormat,
  FILTER_OPERATORS,
  isFilterGroup,
} from "./filterUtils";

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

// `accessMode`: "readOnly" (public API, no write controls at all) or "readWrite" (VPN-only
// API host, write controls appear once the server confirms the permission and the VPN).
const DbInterfacePage = ({ accessMode = ACCESS_MODES.readOnly, isDev = false }) => {
  const isReadWriteLink = accessMode === ACCESS_MODES.readWrite;
  // Reads go to the public API on BOTH links. Only writes need the VPN-only host. Routing
  // reads through the tunnel as well meant the read-write page could not even list its
  // tables until the tunnel woke up, and a dropped VPN made the page unusable rather than
  // just unable to save.
  const readApiTarget = useMemo(
    () => createApiTarget({
      isDev,
      accessMode: ACCESS_MODES.readOnly,
      authenticationScope: AUTHENTICATION_SCOPES.dbInterface,
    }),
    [isDev]
  );
  const writeApiTarget = useMemo(
    () => createApiTarget({ isDev, accessMode, authenticationScope: AUTHENTICATION_SCOPES.dbInterface }),
    [isDev, accessMode]
  );
  const session = useContext(DbInterfaceSessionContext);

  const [columnsRes, setColumnsRes] = useState();
  const [dataRes, setDataRes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tablesList, setTablesList] = useState(false);
  const [tableName, setTableName] = useState(false);
  const columnsMap = useRef({});
  const totalCount = useRef(0);
  const [openErrorToast, setOpenErrorToast] = React.useState();
  const [filterList, setFilterList] = useState([]);
  const [filterGroup, setFilterGroup] = useState(null); // Store complex filter groups
  const [deleteList, setDeleteList] = useState([]);
  const [deleteAll, setDeleteAll] = useState(false);
  const [addItem, setAddItem] = useState(false);
  // Dev-token bypass (see commHelper.jsx / login.jsx) or a live per-tab DB Interface session.
  const isAuthenticated = Boolean(getDevToken()) || Boolean(session.user);

  // Permission state. Read-only until the server answers: never show a write control the
  // server would reject.
  const [userPermissions, setUserPermissions] = useState({
    canRead: true,
    canWrite: false,
    canDelete: false,
    canAdd: false,
    canAccessDev: false,
  });
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  // Server-reported: did this request arrive through the VPN write host?
  const [writeChannelAvailable, setWriteChannelAvailable] = useState(true);
  // Read-write link only: the VPN-only host could not be reached at all (VPN off, DNS, CORS).
  const [writeChannelUnreachable, setWriteChannelUnreachable] = useState(false);
  const [retryCounter, setRetryCounter] = useState(0);
  
  // Filter panel visibility (default: collapsed)
  const [showFilters, setShowFilters] = useState(false);
  
  // Advanced mode state - track pending filters that haven't been applied yet
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(true); // Track if current filters have been applied
  
  // URL state initialization flag
  const urlInitialized = useRef(false);
  // Track if we're loading from URL to prevent clearing filters
  const loadingFromUrl = useRef(false);
  // Track previous tableName to detect actual changes
  const previousTableName = useRef(null);
  // The URL named a table but carried no filters, so the first data request still has to
  // be made by the dedicated effect below.
  const initialUrlLoadPendingRef = useRef(false);

  const refRes = useRef({});

  const take = 100;

  // Fetch user permissions
  const fetchUserPermissions = useCallback(async () => {
    try {
      // Deliberately the write target: this is the call that tells us whether the request
      // reached the server through the VPN host, which is what gates the write controls.
      const res = await getRequest('/getUserPermissions', writeApiTarget);
      if (res && res.data) {
        setUserPermissions(res.data);
        // The server reports false when the request did not come through the VPN host.
        setWriteChannelAvailable(res.data.writeChannelAvailable !== false);
      }
      setWriteChannelUnreachable(false);
    } catch (e) {
      console.error('Error fetching permissions:', e);
      const failure = classifyApiFailure(e);
      if (isReadWriteLink && (failure.kind === API_FAILURE_KINDS.network || failure.kind === API_FAILURE_KINDS.timeout)) {
        setWriteChannelUnreachable(true);
      }
      // Default to read-only on error
      setUserPermissions({
        canRead: true,
        canWrite: false,
        canDelete: false,
        canAdd: false,
        canAccessDev: false,
      });
    }
    setPermissionsLoaded(true);
  }, [writeApiTarget, isReadWriteLink]);

  // Initialize from URL on first load
  useEffect(() => {
    if (!urlInitialized.current) {
      urlInitialized.current = true;
      try {
        const { tableName: urlTable, filters: urlFilters } = parseFiltersFromUrl();
        if (urlTable) {
          // Set flag to prevent tableName effect from clearing filters
          loadingFromUrl.current = true;
          // Set filters first, then tableName to avoid race condition
          if (urlFilters && urlFilters.conditions && urlFilters.conditions.length > 0) {
            // Check if this is a complex filter group (OR operator or nested groups)
            const hasNestedGroups = urlFilters.conditions.some(isFilterGroup);
            const isComplexGroup = urlFilters.operator === 'OR' || hasNestedGroups;
            
            if (isComplexGroup) {
              // Use filterGroup directly for advanced mode (OR groups or nested groups)
              setFilterGroup(urlFilters);
              setFilterList([]); // Clear filterList when using filterGroup
            } else {
              // Convert nested format to flat legacy format for simple mode
              // Map new format operators to legacy format operators
              const operatorMap = {
                'contains': 'ILIKE',
                'notContains': 'notILIKE',
                'equals': '=',
                'notEquals': '!=',
                'in': 'IN',
                'notIn': 'notIn',
                'greaterThan': '>',
                'lessThan': '<',
                'greaterThanOrEqual': '>=',
                'lessThanOrEqual': '<=',
                'between': 'between',
                'isEmpty': 'isEmpty',
                'isNotEmpty': 'isNotEmpty',
                'isNull': 'isNull',
                'isNotNull': 'isNotNull',
              };
              
              const legacyFilters = urlFilters.conditions.map(cond => {
                const legacyOperator = operatorMap[cond.operator] || cond.operator;
                let legacyValue = cond.value;
                
                // For contains/notContains, wrap value with % signs for legacy format
                if (cond.operator === 'contains' || cond.operator === 'notContains') {
                  if (typeof legacyValue === 'string' && !legacyValue.startsWith('%')) {
                    legacyValue = `%${legacyValue}%`;
                  }
                }
                
                // For 'in' operator, convert array of IDs to array of objects with _id
                if (cond.operator === 'in' && Array.isArray(legacyValue)) {
                  legacyValue = legacyValue.map(id => ({ _id: id }));
                }
                
                return {
                  column: cond.field,
                  value: legacyValue,
                  condition: { 
                    value: legacyOperator, 
                    label: FILTER_OPERATORS[cond.operator]?.label || cond.operator 
                  }
                };
              });
              setFilterList(legacyFilters);
              setFilterGroup(null); // Clear filterGroup when using filterList
            }
          }
          // With filters in the URL, setting them re-runs the filter effect below and that
          // performs the first fetch. Without filters nothing changes after this point:
          // the filter effects never re-run and the table effect sees previousTableName
          // already equal to the table, so the initial load has to be requested explicitly.
          const urlCarriedFilters = Boolean(
            urlFilters && urlFilters.conditions && urlFilters.conditions.length > 0
          );
          if (!urlCarriedFilters) {
            initialUrlLoadPendingRef.current = true;
          }
          // Set tableName after filters to ensure filters are preserved
          previousTableName.current = urlTable;
          setTableName(urlTable);
          // Reset flag after a short delay to allow effects to process
          setTimeout(() => {
            loadingFromUrl.current = false;
          }, 100);
        } else if (urlFilters && urlFilters.conditions && urlFilters.conditions.length > 0) {
          // If we have filters but no table, still set the filters
          // Map new format operators to legacy format operators
          const operatorMap = {
            'contains': 'ILIKE',
            'notContains': 'notILIKE',
            'equals': '=',
            'notEquals': '!=',
            'in': 'IN',
            'notIn': 'notIn',
            'greaterThan': '>',
            'lessThan': '<',
            'greaterThanOrEqual': '>=',
            'lessThanOrEqual': '<=',
            'between': 'between',
            'isEmpty': 'isEmpty',
            'isNotEmpty': 'isNotEmpty',
            'isNull': 'isNull',
            'isNotNull': 'isNotNull',
          };
          
          const legacyFilters = urlFilters.conditions.map(cond => {
            const legacyOperator = operatorMap[cond.operator] || cond.operator;
            let legacyValue = cond.value;
            
            // For contains/notContains, wrap value with % signs for legacy format
            if (cond.operator === 'contains' || cond.operator === 'notContains') {
              if (typeof legacyValue === 'string' && !legacyValue.startsWith('%')) {
                legacyValue = `%${legacyValue}%`;
              }
            }
            
            // For 'in' operator, convert array of IDs to array of objects with _id
            if (cond.operator === 'in' && Array.isArray(legacyValue)) {
              legacyValue = legacyValue.map(id => ({ _id: id }));
            }
            
            return {
              column: cond.field,
              value: legacyValue,
              condition: { 
                value: legacyOperator, 
                label: FILTER_OPERATORS[cond.operator]?.label || cond.operator 
              }
            };
          });
          setFilterList(legacyFilters);
        }
      } catch (e) {
        console.error('Error parsing URL filters:', e);
        setOpenErrorToast('Invalid filter URL. Filters have been reset.');
      }
    }
  }, []);

  // Update URL when filters change
  const updateUrl = useCallback(() => {
    if (tableName && urlInitialized.current) {
      // In advanced mode, use filterGroup directly; otherwise convert filterList
      let filterGroupForUrl = null;
      if (filterGroup && filterGroup.conditions && filterGroup.conditions.length > 0) {
        filterGroupForUrl = filterGroup;
      } else if (filterList.length > 0) {
        // Convert legacy filter format to new format for URL
        filterGroupForUrl = migrateFromLegacyFormat(filterList);
      }
      updateUrlWithFilters(tableName, filterGroupForUrl);
    }
  }, [tableName, filterList, filterGroup]);

  // Debounced URL update
  useEffect(() => {
    const timer = setTimeout(updateUrl, 500);
    return () => clearTimeout(timer);
  }, [updateUrl]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      try {
        const { tableName: urlTable, filters: urlFilters } = parseFiltersFromUrl();
        if (urlTable && urlTable !== tableName) {
          setTableName(urlTable);
        }
        if (urlFilters && urlFilters.conditions) {
          // Check if this is a complex filter group (OR operator or nested groups)
          const hasNestedGroups = urlFilters.conditions.some(isFilterGroup);
          const isComplexGroup = urlFilters.operator === 'OR' || hasNestedGroups;
          
          if (isComplexGroup) {
            // Use filterGroup directly for advanced mode (OR groups or nested groups)
            setFilterGroup(urlFilters);
            setFilterList([]); // Clear filterList when using filterGroup
          } else {
            // Map new format operators to legacy format operators
            const operatorMap = {
              'contains': 'ILIKE',
              'notContains': 'notILIKE',
              'equals': '=',
              'notEquals': '!=',
              'in': 'IN',
              'notIn': 'notIn',
              'greaterThan': '>',
              'lessThan': '<',
              'greaterThanOrEqual': '>=',
              'lessThanOrEqual': '<=',
              'between': 'between',
              'isEmpty': 'isEmpty',
              'isNotEmpty': 'isNotEmpty',
              'isNull': 'isNull',
              'isNotNull': 'isNotNull',
            };
            
            const legacyFilters = urlFilters.conditions.map(cond => {
              const legacyOperator = operatorMap[cond.operator] || cond.operator;
              let legacyValue = cond.value;
              
              // For contains/notContains, wrap value with % signs for legacy format
              if (cond.operator === 'contains' || cond.operator === 'notContains') {
                if (typeof legacyValue === 'string' && !legacyValue.startsWith('%')) {
                  legacyValue = `%${legacyValue}%`;
                }
              }
              
              // For 'in' operator, convert array of IDs to array of objects with _id
              if (cond.operator === 'in' && Array.isArray(legacyValue)) {
                legacyValue = legacyValue.map(id => ({ _id: id }));
              }
              
              return {
                column: cond.field,
                value: legacyValue,
                condition: { 
                  value: legacyOperator, 
                  label: FILTER_OPERATORS[cond.operator]?.label || cond.operator 
                }
              };
            });
            setFilterList(legacyFilters);
            setFilterGroup(null); // Clear filterGroup when using filterList
          }
        } else if (!urlFilters) {
          setFilterList([]);
          setFilterGroup(null);
        }
      } catch (e) {
        console.error('Error handling popstate:', e);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [tableName]);

  useEffect(() => {
    // Only fetch data if:
    // 1. tableName is set
    // 2. URL initialization is complete (to avoid race conditions)
    // 3. User is authenticated (to avoid auth errors)
    // 4. NOT in advanced mode (advanced mode requires explicit "Apply Filters" click)
    if (tableName && urlInitialized.current && isAuthenticated) {
      // In advanced mode, don't auto-fetch - wait for "Apply Filters" button
      if (isAdvancedMode) {
        // Mark filters as not applied yet
        setFiltersApplied(false);
        return;
      }
      // In simple mode, fetch data immediately when filterList changes
      resetData();
      getData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch is intentionally driven by filterList/auth only; getData/tableName/isAdvancedMode are read via closure and adding them would re-fetch on unrelated changes (table change is handled by its own effect)
  }, [filterList, isAuthenticated]);
  
  // Separate effect for filterGroup changes from URL loading (auto-apply when loading from URL)
  useEffect(() => {
    if (tableName && urlInitialized.current && isAuthenticated && filterGroup && !isAdvancedMode) {
      // Only auto-apply filterGroup when loaded from URL (not in advanced mode)
      resetData();
      getData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-apply is intentionally driven by filterGroup/auth only; getData/tableName/isAdvancedMode are read via closure and adding them would re-fetch on unrelated changes
  }, [filterGroup, isAuthenticated]);

  useEffect(() => {}, []);

  useEffect(() => {
    const getTablesName = async () => {
      const res = await getPost(`/getTablesList`, null, readApiTarget)
        .then((res) => res.data)
        .catch(() => []);

      // Backend returns only the tables this user may access (admins get all).
      // Guard against a non-array (e.g. error payload) so the dropdown never crashes.
      setTablesList(Array.isArray(res) ? res : []);
    };
    if (isAuthenticated) {
      getTablesName();
      fetchUserPermissions();
    }
    // retryCounter: the VPN banner's Retry button re-runs this bootstrap.
  }, [isAuthenticated, fetchUserPermissions, readApiTarget, retryCounter]);
  const resetData = () => {
    setDeleteList([]);
    setDeleteAll(false);
    setColumnsRes(null);
    setDataRes([]);
    columnsMap.current = {};
    refRes.current = {};
    totalCount.current = 0;
  };

  useEffect(() => {
    // Only clear filters and fetch data if:
    // 1. tableName is set
    // 2. We're not loading from URL (to preserve URL filters)
    // 3. User is authenticated (to avoid auth errors)
    // 4. tableName actually changed (not just set for the first time)
    if (tableName && !loadingFromUrl.current && isAuthenticated && previousTableName.current !== tableName) {
      // Clear filters only when table actually changes (user selects different table)
      setFilterList([]);
      resetData();
      getData(true);
      previousTableName.current = tableName;
    } else if (tableName && previousTableName.current === null) {
      // First time setting tableName (from URL or initial load)
      previousTableName.current = tableName;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs on tableName/auth change only; getData is read via closure and adding it would re-fetch every time dataRes updates (infinite loop)
  }, [tableName, isAuthenticated]);

  // First load of a URL that names a table without filters. Until Sep 2026 this happened by
  // accident: isAuthenticated started false and flipped to true once Firebase restored the
  // session, and that flip re-ran the filter effect after the table was set. The session
  // gate now hands the page an already-signed-in user, so isAuthenticated never changes and
  // nothing requested the data — the page sat on its loader forever.
  useEffect(() => {
    if (!initialUrlLoadPendingRef.current) return;
    if (!tableName || !isAuthenticated) return;
    initialUrlLoadPendingRef.current = false;
    resetData();
    getData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot initial load; getData/resetData are read via closure
  }, [tableName, isAuthenticated]);

  const deleteItemsFromDb = async () => {
    setSaving(true);
    try {
      await getPost(
        `/deleteItemsFromDb`,
        {
          tableName,
          ids: deleteList,
          filters: filterList,
          deleteAll,
        },
        writeApiTarget
      );
      setDeleteAll(false);
      resetData();
      getData(true);
    } catch (e) {
      console.log({ e });
      const message = getUserFacingApiErrorMessage(e);
      if (message) setOpenErrorToast(message);
    }

    setSaving(false);
  };

  const getData = async (resetData) => {
    // Use filterGroup if available (for complex OR/nested filters), otherwise use filterList
    const filtersToSend = filterGroup || filterList;
    
    try {
      const res = await getPost(
        `/getInterfaceDetails`,
        // `http://localhost:8080/getInterfaceDetails`
        {
          tableName,
          skip: resetData ? 0 : dataRes?.length || 0,
          take,
          totalCount: totalCount.current,
          filters: filtersToSend,
        },
        readApiTarget
      );
      
      if (res.data?.error) {
        setOpenErrorToast(res.data.error);
        return;
      }
      
      if (!columnsRes || columnsRes.length === 0 || resetData) {
        if (!res.data?.columns) {
          setOpenErrorToast('Invalid response: missing columns');
          return;
        }
        setColumnsRes(res.data.columns);
        const newColumnsMap = {};
        res.data.columns.forEach((i) => {
          newColumnsMap[i.name] = i;
        });
        columnsMap.current = newColumnsMap;
      }
      totalCount.current = res.data.countRows;
      if (resetData) {
        setDataRes(res.data.data);
      } else {
        setDataRes(dataRes.concat(res.data.data));
      }
    } catch (e) {
      console.error('Error fetching data:', e);
      const message = getUserFacingApiErrorMessage(e);
      if (message) setOpenErrorToast(message);
    }
  };

  // Apply filters function for advanced mode - called when user clicks "Apply Filters"
  const applyFilters = useCallback(() => {
    if (tableName && isAuthenticated) {
      setFiltersApplied(true);
      resetData();
      getData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getData is read via closure (it is recreated each render and not memoized); adding it would change this callback's identity every render
  }, [tableName, isAuthenticated, filterGroup]);

  // Handler for when advanced mode is toggled
  const handleAdvancedModeChange = useCallback((isAdvanced) => {
    setIsAdvancedMode(isAdvanced);
    // If switching to simple mode, auto-apply any pending filters
    if (!isAdvanced && !filtersApplied) {
      setFiltersApplied(true);
      resetData();
      getData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getData is read via closure (it is recreated each render and not memoized); adding it would change this callback's identity every render
  }, [filtersApplied]);

  const saveItem = async (newItem) => {
    setSaving(true);
    await getPost("/addItemsToDb", { tableName, item: newItem }, writeApiTarget).catch(
      (e) => {
        console.log({ e });
        const message = getUserFacingApiErrorMessage(e);
        if (message) setOpenErrorToast(message);
      }
    );
    setSaving(false);
    setAddItem(false);
    resetData();
    getData(true);
  };

  // Write controls exist only on the read-write link, and only once the server has granted
  // the permission for a request that came through the VPN host.
  const canWrite = isReadWriteLink && userPermissions.canWrite;
  const canAdd = isReadWriteLink && userPermissions.canAdd;
  const canDelete = isReadWriteLink && userPermissions.canDelete;
  const isReadOnly = !canWrite;
  const effectivePermissions = { ...userPermissions, canWrite, canAdd, canDelete };

  return (
    <Wrapper isDev={isDev} accessMode={accessMode}>
      {addItem && canAdd ? (
        <AddItemPage
          apiTarget={readApiTarget}
          writeApiTarget={writeApiTarget}
          tableName={tableName}
          resRef={refRes}
          dataRes={dataRes}
          saving={saving}
          onCancel={() => {
            setAddItem(false);
          }}
          onSave={saveItem}
          columnsRes={columnsRes}
        />
      ) : (
        <Wrapper>
          <DbInterfaceSessionBar
            accessMode={accessMode}
            isDev={isDev}
            permissionsLoaded={permissionsLoaded}
            canWrite={canWrite}
          />
          {isReadWriteLink && writeChannelUnreachable && (
            <DbInterfaceVpnBanner
              variant={VPN_BANNER_VARIANTS.unreachable}
              isDev={isDev}
              onRetry={() => setRetryCounter((counter) => counter + 1)}
            />
          )}
          {isReadWriteLink && !writeChannelUnreachable && permissionsLoaded && !writeChannelAvailable && (
            <DbInterfaceVpnBanner variant={VPN_BANNER_VARIANTS.notViaVpn} isDev={isDev} />
          )}
          {tablesList && (
            <Row>
              <Select
                className="basic-single"
                classNamePrefix="select"
                placeholder="Table name"
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    width: 300,
                  }),
                }}
                value={!tableName ? null : { label: tableName }}
                onChange={(event) => {
                  setTableName(event.value);
                }}
                isClearable={false}
                isSearchable
                options={
                  tablesList?.map((item) => ({ value: item, label: item })) ||
                  []
                }
              />
              <div style={{ margin: "0 10px" }}>{totalCount.current}</div>
              <FontAwesomeIcon
                size={"lg"}
                color={"#4b4b4b"}
                icon={faSyncAlt}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  resetData();
                  getData(true);
                }}
              />

              {/* Filter toggle button */}
              <FilterToggleButton 
                onClick={() => setShowFilters(!showFilters)}
                active={showFilters}
              >
                <FontAwesomeIcon icon={faFilter} style={{ marginRight: "6px" }} />
                Filters
                {(filterList.length > 0 || (filterGroup?.conditions?.length > 0)) && (
                  <FilterBadge>
                    {filterList.length > 0 ? filterList.length : filterGroup?.conditions?.length}
                  </FilterBadge>
                )}
              </FilterToggleButton>

              {/* Only show Add Item button if user has write permission */}
              {columnsRes && canAdd && (
                <Button
                  style={{
                    margin: "0 20px",
                    color: "white",
                    backgroundColor: saving ? "grey" : "#4eb7f5",
                  }}
                  onClick={() => {
                    setAddItem(true);
                  }}
                >
                  {`+ Add Item`}
                </Button>
              )}

              {/* Only show Delete button if user has delete permission */}
              {(deleteAll || deleteList.length > 0) && canDelete && (
                <Button
                  style={{
                    margin: "0 20px",
                    color: "white",
                    backgroundColor: saving ? "grey" : "#f66",
                  }}
                  onClick={() => {
                    if (!saving) deleteItemsFromDb();
                  }}
                >
                  {`Delete ${
                    deleteAll ? totalCount.current : deleteList.length
                  } Items`}
                </Button>
              )}

              <div>{saving ? "Saving" : ""}</div>
            </Row>
          )}
          {columnsMap && showFilters && (
            <FilterInterface
              refRes={refRes}
              columnsMap={columnsMap}
              dataRes={dataRes}
              onRemoveFilter={(index) => {
                const newFilterList = [...filterList];
                // Check if removing a special filter group marker
                if (newFilterList[index]?.column === '__FILTER_GROUP__') {
                  setFilterGroup(null);
                }
                newFilterList.splice(index, 1);
                setFilterList(newFilterList);
              }}
              filterList={filterList}
              onFilterAdd={(filterData, index) => {
                // Check if this is a special filter group marker
                if (filterData.column === '__FILTER_GROUP__' && filterData.value) {
                  setFilterGroup(filterData.value);
                  return; // Don't add to filterList
                }
                
                const newFilterList = [...filterList];

                if (index > -1) {
                  newFilterList[index] = filterData;
                } else {
                  newFilterList.push(filterData);
                }
                setFilterList(newFilterList);
                // Clear filterGroup when adding regular filters
                if (filterGroup) {
                  setFilterGroup(null);
                }
              }}
              columns={columnsRes}
              apiTarget={readApiTarget}
              tableName={tableName}
              filterGroup={filterGroup}
              onFilterGroupChange={(newGroup) => {
                setFilterGroup(newGroup);
                // Clear filterList when using filterGroup
                setFilterList([]);
                // Mark filters as not applied in advanced mode
                if (isAdvancedMode) {
                  setFiltersApplied(false);
                }
              }}
              onApplyFilters={applyFilters}
              onAdvancedModeChange={handleAdvancedModeChange}
              isAdvancedMode={isAdvancedMode}
              filtersApplied={filtersApplied}
            />
          )}
          {columnsRes && dataRes ? (
            <DbTableData
              apiTarget={readApiTarget}
              writeApiTarget={writeApiTarget}
              columnsRes={columnsRes}
              dataRes={dataRes}
              getData={getData}
              totalCount={totalCount}
              setSaving={setSaving}
              columnsMap={columnsMap}
              refRes={refRes}
              tableName={tableName}
              setDataRes={setDataRes}
              setOpenErrorToast={setOpenErrorToast}
              take={take}
              deleteList={deleteList}
              setDeleteList={setDeleteList}
              setDeleteAll={setDeleteAll}
              deleteAll={deleteAll}
              isReadOnly={isReadOnly}
              userPermissions={effectivePermissions}
            />
          ) : tableName ? (
            <Loader type="TailSpin" color="#00BFFF" height={100} width={100} />
          ) : (
            <TablesWrapView
              tablesList={tablesList}
              onTableClick={(tableName) => {
                setTableName(tableName);
              }}
            />
          )}
          <Snackbar
            open={!!openErrorToast}
            autoHideDuration={6000}
            onClose={() => setOpenErrorToast(null)}
          >
            <Alert onClose={() => setOpenErrorToast(null)} severity="error">
              {openErrorToast}
            </Alert>
          </Snackbar>
        </Wrapper>
      )}
    </Wrapper>
  );
};

export default DbInterfacePage;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: ${({ isDev }) => (isDev ? "#ff100010" : null)};
  /* The read-write link is visibly different: a red stripe along the top. */
  border-top: ${({ accessMode }) => (accessMode === ACCESS_MODES.readWrite ? "4px solid #d32f2f" : "none")};
`;

const Row = styled.div`
  display: -webkit-box;
  flex-direction: row;
  z-index: 99999;
  align-items: center;
`;

const FilterToggleButton = styled.button`
  display: flex;
  align-items: center;
  margin: 0 15px;
  padding: 8px 14px;
  background-color: ${({ active }) => (active ? '#4eb7f5' : '#f5f5f5')};
  border: 1px solid ${({ active }) => (active ? '#4eb7f5' : '#ddd')};
  border-radius: 6px;
  color: ${({ active }) => (active ? 'white' : '#4b4b4b')};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ active }) => (active ? '#3a9ad9' : '#e8e8e8')};
  }
`;

const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 8px;
  background-color: ${({ theme }) => theme === 'active' ? 'rgba(255,255,255,0.3)' : '#4eb7f5'};
  border-radius: 9px;
  color: white;
  font-size: 11px;
  font-weight: 600;
`;
